/**
 * Partida ao vivo, contra uma pessoa.
 *
 * Mesma interface do modo demonstração, e é essa a diferença que importa:
 * a tela do tabuleiro não sabe se o adversário é um bot no navegador ou
 * alguém do outro lado do país.
 *
 * Aqui o navegador não arbitra nada. Ele desenha o que o servidor mandou e
 * pede o que o jogador clicou — o Durable Object da partida decide se o
 * lance vale, quanto tempo custou e quem ganhou.
 */
import type { Outcome, Seat } from '@engine/games/types.ts';

import type { GameType, MatchAdapter, MatchSnapshot } from './adapter.ts';
import { baseDaApi } from './presenca.ts';

export interface LiveMatchOptions {
  readonly matchId: string;
  readonly token: string;
  readonly gameType: GameType;
  readonly opponentName: string;
}

/** Mensagens que o servidor envia. */
interface MensagemServidor {
  type?: string;
  seat?: number;
  turn?: number;
  state?: string;
  legalMoves?: string[];
  remainingMs?: [number, number];
  lastMove?: string;
  outcome?: Outcome;
  code?: string;
  message?: string;
}

const RECONEXOES_MAX = 5;

export function createLiveMatch(options: LiveMatchOptions): MatchAdapter {
  const base = baseDaApi();
  if (!base) throw new Error('API não configurada: defina VITE_API_BASE_URL');

  const url = new URL(`${base.replace(/^http/, 'ws')}/realtime`);
  url.searchParams.set('matchId', options.matchId);
  url.searchParams.set('token', options.token);

  const listeners = new Set<(snapshot: MatchSnapshot) => void>();
  let socket: WebSocket | null = null;
  let descartado = false;
  let tentativas = 0;
  let reconexao: ReturnType<typeof setTimeout> | null = null;

  let snapshot: MatchSnapshot = {
    gameType: options.gameType,
    state: '',
    turn: 0,
    mySeat: 0,
    legalMoves: [],
    remainingMs: [0, 0],
    outcome: null,
    opponentName: options.opponentName,
    lastMove: null,
    error: null,
  };

  function emitir(mudanca: Partial<MatchSnapshot>): void {
    snapshot = { ...snapshot, ...mudanca };
    for (const listener of listeners) listener(snapshot);
  }

  function aplicar(mensagem: MensagemServidor): void {
    switch (mensagem.type) {
      case 'JOINED':
        tentativas = 0;
        emitir({
          mySeat: (mensagem.seat ?? 0) as Seat,
          turn: (mensagem.turn ?? 0) as Seat,
          state: mensagem.state ?? '',
          legalMoves: mensagem.legalMoves ?? [],
          remainingMs: mensagem.remainingMs ?? [0, 0],
          error: null,
        });
        return;

      case 'STATE':
        emitir({
          turn: (mensagem.turn ?? 0) as Seat,
          state: mensagem.state ?? '',
          legalMoves: mensagem.legalMoves ?? [],
          remainingMs: mensagem.remainingMs ?? [0, 0],
          lastMove: mensagem.lastMove ?? null,
          error: null,
        });
        return;

      case 'FINISHED':
        emitir({
          outcome: mensagem.outcome ?? null,
          legalMoves: [],
          ...(mensagem.state ? { state: mensagem.state } : {}),
        });
        return;

      case 'ERROR':
        // Recusa de lance não derruba a partida: mostra o motivo e segue.
        emitir({ error: legivel(mensagem.code, mensagem.message) });
        return;

      default:
        return;
    }
  }

  function legivel(codigo?: string, mensagem?: string): string {
    switch (codigo) {
      case 'NOT_YOUR_TURN':
        return 'Não é a sua vez.';
      case 'ILLEGAL_MOVE':
      case 'INVALID_MOVE':
        return 'Lance inválido.';
      case 'MATCH_FINISHED':
        return 'A partida já terminou.';
      case 'UNAUTHORIZED':
        return 'Sua sessão expirou. Entre de novo.';
      case 'NOT_A_PLAYER':
        return 'Você não está nesta partida.';
      default:
        return mensagem ?? 'Não foi possível processar o lance.';
    }
  }

  function conectar(): void {
    if (descartado) return;

    socket = new WebSocket(url.toString());

    socket.addEventListener('message', (evento) => {
      try {
        aplicar(JSON.parse(String(evento.data)) as MensagemServidor);
      } catch {
        // Mensagem que não é JSON não deveria existir; ignorar é melhor
        // que derrubar a partida por causa dela.
      }
    });

    socket.addEventListener('close', () => {
      if (descartado || snapshot.outcome) return;

      // O relógio continua correndo no servidor durante a queda, e passar
      // da tolerância vira abandono. Reconectar rápido é o que evita perder
      // a partida por causa da rede.
      if (tentativas >= RECONEXOES_MAX) {
        emitir({ error: 'Conexão perdida. Recarregue a página.' });
        return;
      }
      const espera = Math.min(1_000 * 2 ** tentativas, 8_000);
      tentativas += 1;
      emitir({ error: 'Reconectando…' });
      reconexao = setTimeout(conectar, espera);
    });
  }

  conectar();

  function enviar(payload: unknown): void {
    if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify(payload));
  }

  return {
    subscribe(listener) {
      listeners.add(listener);
      listener(snapshot);
      return () => listeners.delete(listener);
    },

    play(move) {
      // `clientTs` é informativo: o servidor cronometra pelo próprio relógio
      // e usa isto só para detectar divergência.
      enviar({ type: 'MOVE', move, clientTs: new Date().toISOString() });
    },

    resign() {
      enviar({ type: 'RESIGN' });
    },

    dispose() {
      descartado = true;
      if (reconexao) clearTimeout(reconexao);
      listeners.clear();
      socket?.close();
    },
  };
}
